/**
 * O filtro da tela de Estoque, escrito para o papel (F150).
 *
 * Função pura, longe do React, porque é ela que decide se o relatório mente.
 * Um papel que não diz "abaixo do mínimo" é lido como o estoque inteiro, e
 * alguém compra em cima disso.
 */

export type StockScreenFilters = {
  readonly search: string
  readonly situacao: 'all' | 'with' | 'negative' | 'below'
  /** A família da peça (F163). Vazia é "todas as categorias". */
  readonly categoryId?: string | undefined
  /** O nome dela, para o papel — o papel não consulta cadastro nenhum. */
  readonly categoryName?: string | undefined
  /** A marca (F167). Vazia é "todas as marcas". */
  readonly brand?: string | undefined
  /**
   * Onde a busca procurou (F167).
   *
   * Vai junto porque o papel refaz a consulta: com "começa com" na tela e
   * ausente aqui, o relatório sairia com MAIS linhas do que a pessoa estava
   * vendo, e com um total que não bate com o da tela.
   */
  readonly searchMode?: 'anywhere' | 'prefix' | 'exact' | undefined
}

const SITUATION_LABEL: Record<StockScreenFilters['situacao'], string | null> = {
  all: null,
  with: 'somente com saldo',
  negative: 'a acertar (saldo negativo)',
  below: 'abaixo do mínimo',
}

/**
 * O que estava filtrado, por extenso.
 *
 * Vazio quando nada estava — e quem imprime o papel escreve "sem filtro", que
 * é diferente de não dizer nada.
 */
export const stockFilterLabels = (
  filters: StockScreenFilters
): readonly string[] => {
  const labels: string[] = []

  const search = filters.search.trim()
  if (search.length > 0) labels.push(`busca "${search}"`)

  const situation = SITUATION_LABEL[filters.situacao]
  if (situation !== null) labels.push(situation)

  // F163: sem esta linha, o papel de "Bateria" é lido como o estoque inteiro.
  const category = filters.categoryName?.trim() ?? ''
  if (category.length > 0) labels.push(`categoria ${category}`)

  const brand = filters.brand?.trim() ?? ''
  if (brand.length > 0) labels.push(`marca ${brand}`)

  /*
   * Dito por extenso: duas buscas pelo mesmo texto devolvem listas diferentes,
   * e quem pega o papel amanhã não tem como saber qual delas foi.
   *
   * A frase do `prefix` dizia "só no começo do NOME" e estava errada desde a
   * F167 — o SQL aplica o prefixo às três colunas, nome, código e código de
   * barras. Um papel que descreve mal o próprio recorte é pior que um papel
   * sem recorte escrito.
   */
  if (search.length > 0 && filters.searchMode === 'prefix') {
    labels.push('só no começo do nome, do código ou do código de barras')
  }
  if (search.length > 0 && filters.searchMode === 'exact') {
    labels.push('código ou código de barras exato')
  }

  return labels
}
